import React, { PureComponent } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Drawer from '@material-ui/core/Drawer'
import {
  mdiAlphaABox,
  mdiAlphaCBox,
  mdiAlphaSBox,
  mdiAngleAcute,
  mdiAngleRight,
  mdiAnimationOutline,
  mdiArrowSplitHorizontal,
  mdiEyeOff,
  mdiChartHistogram,
  mdiCheckboxIntermediate,
  mdiChevronLeft,
  mdiChevronRight,
  mdiCrosshairsGps,
  mdiContentCut,
  mdiCubeOutline,
  mdiCursorDefault,
  mdiCursorPointer,
  mdiDatabase,
  mdiCloudLock,
  mdiCloudCheck,
  mdiInboxArrowDown,
  mdiHistory,
  mdiClipboardListOutline,
  mdiInvertColors,
  mdiCamera,
  mdiShareVariant,
  mdiAutoFix,
  mdiDownload,
  mdiEllipse,
  mdiEyedropper,
  mdiExitToApp,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiGesture,
  mdiInformationOutline,
  mdiBell,
  mdiMagnify,
  mdiRectangle,
  mdiRefresh,
  mdiRuler,
  mdiTools,
  mdiVectorLink,
  mdiVideo,
  mdiViewColumn,
  mdiViewGridPlusOutline,
  mdiWhiteBalanceSunny,
  mdiHospitalBox,
  mdiImageOutline,
  mdiLayers,
  mdiFileCad,
  mdiFileDocument,
  mdiMonitor,
  mdiMicrophone,
  mdiMicrophoneOff,
  mdiPlusBox,
} from '@mdi/js'
import CircularProgress from '@material-ui/core/CircularProgress'
import Collapse from '@material-ui/core/Collapse'
import Divider from '@material-ui/core/Divider'
import WWPresetManager from './WWPresetManager'
import DsaMenu from './DsaMenu'
import packageJson from '../../package.json'
import {
  isStudyLoadActive,
  isStudyLoadLocal,
  studyLoadSourceDownloadTitle,
  showStudyLoadOriginHint,
  formatStudyLoadOriginLabel,
} from '../utils/studyLoadSource'
import GatewayStatusBadge from './GatewayStatusBadge'
import PortalDisclaimerRail from './PortalDisclaimerRail'
import { portalSectionVisible } from '../plan/portalChrome'
import { getPwaInstallLabel } from '../utils/pwaInstallPrompt'
import {
  RENDER_MODE,
} from '../utils/renderMode'
import { PET_CLUT_IDS, PET_CLUT_LABELS } from '../utils/petClut'
import DoseBadge from './dose/DoseBadge'
import { t } from '../i18n'
import { attachBoostedWheelScroll } from '../utils/panelWheelScroll'

const GROUP = {
  system: '#64B5F6',
  navigation: '#81C784',
  image: '#FFB74D',
  study: '#4DD0E1',
  measure: '#E57373',
  roi: '#BA68C8',
}

const iconSize = 0.95

// Inset desde el borde: evita la franja de gestos del SO/navegador (atrás, edge, etc.)
// que se come los toques con right:0, sobre todo fuera de fullscreen.
const EDGE_SAFE_RIGHT = 'max(12px, env(safe-area-inset-right, 0px))'
const EDGE_SAFE_LEFT = 'max(10px, env(safe-area-inset-left, 0px))'
// Bajo overlays de info DICOM (mrtopleft ~ paciente)
const LAYOUT_FAB_TOP = 'max(56px, 11vh)'

const styles = {
  // Zona de toque amplia; el chrome azul queda inset (no “metido” en el borde).
  tabHit: {
    position: 'fixed',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 150100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    // Padding derecho = zona muerta del SO; padding izquierdo = hit extra sobre el canvas
    paddingRight: EDGE_SAFE_RIGHT,
    paddingLeft: 14,
    paddingTop: 10,
    paddingBottom: 10,
    margin: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  tabFace: {
    width: 32,
    minHeight: 80,
    padding: '14px 4px',
    borderRadius: 10,
    background: 'rgba(21, 101, 192, 0.94)',
    boxShadow: '-2px 0 10px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: '#fff',
    boxSizing: 'border-box',
    pointerEvents: 'none',
  },
  paper: {
    width: 108,
    maxWidth: '42vw',
    height: '100%',
    background: 'rgba(18, 18, 28, 0.96)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    padding: '10px 6px 10px',
    boxSizing: 'border-box',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  /** Studio Rail: columna fija (sin overlay) */
  dockedPaper: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    background: 'transparent',
    color: '#fff',
    padding: '8px 6px 10px',
    boxSizing: 'border-box',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    border: 'none',
  },
  version: {
    marginTop: 'auto',
    paddingTop: 10,
    paddingBottom: 4,
    textAlign: 'center',
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: 0.4,
    color: 'rgba(255, 255, 255, 0.38)',
    userSelect: 'none',
    lineHeight: 1.2,
    flexShrink: 0,
  },
  footerRow: {
    marginTop: 'auto',
    paddingTop: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    gap: 4,
  },
  footerMeta: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: '100%',
  },
  versionText: {
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: 0.4,
    color: 'rgba(255, 255, 255, 0.38)',
    userSelect: 'none',
    lineHeight: 1.2,
  },
  infoBtn: {
    padding: 4,
    color: 'rgba(255, 255, 255, 0.55)',
  },
  infoBtnActive: {
    color: '#64B5F6',
  },
  statsPanel: {
    width: '100%',
    maxHeight: 220,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '6px 4px 4px',
    marginBottom: 2,
    boxSizing: 'border-box',
    borderRadius: 6,
    background: 'rgba(0, 0, 0, 0.35)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    fontSize: 9,
    lineHeight: 1.35,
    color: 'rgba(255, 255, 255, 0.82)',
    textAlign: 'left',
    WebkitOverflowScrolling: 'touch',
  },
  statsTitle: {
    fontWeight: 600,
    fontSize: 10,
    marginBottom: 4,
    color: 'rgba(255, 255, 255, 0.92)',
  },
  statsLine: {
    marginBottom: 3,
    wordBreak: 'break-word',
  },
  statsHighlight: {
    marginBottom: 3,
    wordBreak: 'break-word',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.95)',
    fontSize: 10,
  },
  statsMuted: {
    opacity: 0.85,
  },
  statsClearBtn: {
    marginTop: 6,
    width: '100%',
    padding: '4px 2px',
    fontSize: 8,
    lineHeight: 1.2,
    color: 'rgba(255,255,255,0.7)',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 4,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  statsOfflineOk: {
    color: '#81C784',
    fontWeight: 600,
  },
  qaBadge: {
    display: 'inline-block',
    marginRight: 6,
    padding: '1px 5px',
    borderRadius: 3,
    fontSize: 8,
    fontWeight: 600,
    letterSpacing: 0.2,
    lineHeight: 1.4,
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid transparent',
    WebkitTapHighlightColor: 'transparent',
  },
  qaBadgeOk: {
    color: '#a5d6a7',
    background: 'rgba(76, 175, 80, 0.18)',
    borderColor: 'rgba(76, 175, 80, 0.35)',
  },
  qaBadgeUnverified: {
    color: '#ffe082',
    background: 'rgba(255, 193, 7, 0.14)',
    borderColor: 'rgba(255, 193, 7, 0.35)',
  },
  gatewayBadge: {
    display: 'inline-block',
    marginRight: 6,
    padding: '1px 5px',
    borderRadius: 3,
    fontSize: 8,
    fontWeight: 600,
    letterSpacing: 0.2,
    lineHeight: 1.4,
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid transparent',
    width: 'auto',
    WebkitTapHighlightColor: 'transparent',
  },
  modeToggleWrap: {
    marginTop: 10,
    marginBottom: 4,
    padding: '0 2px',
    flexShrink: 0,
  },
  modeToggleLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    margin: '0 4px 6px',
    color: GROUP.system,
    opacity: 0.95,
  },
  modeToggleTrack: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(0,0,0,0.28)',
  },
  modeToggleOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: '100%',
    minHeight: 40,
    padding: '6px 4px',
    margin: 0,
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'transparent',
    color: 'rgba(255,255,255,0.55)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.2,
    lineHeight: 1.15,
    transition: 'background 0.12s ease, color 0.12s ease',
  },
  modeToggleOptionLast: {
    borderBottom: 'none',
  },
  modeToggleOptionActive: {
    background: 'rgba(100, 181, 246, 0.28)',
    color: '#fff',
    boxShadow: 'inset 0 0 0 1px rgba(100, 181, 246, 0.55)',
  },
  modeToggleOptionActivePreview: {
    background: 'rgba(255, 183, 77, 0.22)',
    color: '#FFE0B2',
    boxShadow: 'inset 0 0 0 1px rgba(255, 183, 77, 0.55)',
  },
  groupTitle: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    margin: '8px 4px 2px',
    opacity: 0.95,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 2,
    alignItems: 'center',
    justifyItems: 'center',
  },
  btn: {
    padding: 8,
    minWidth: 44,
    minHeight: 44,
    width: '100%',
    boxSizing: 'border-box',
    touchAction: 'manipulation',
  },
  activeRing: {
    boxShadow: '0 0 0 2px currentColor',
    borderRadius: 8,
  },
  // Quick icons discretos con panel colapsado
  ghostRail: {
    position: 'fixed',
    right: EDGE_SAFE_RIGHT,
    top: 'max(72px, 22vh)',
    zIndex: 150090,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    pointerEvents: 'none',
  },
  ghostBtn: {
    pointerEvents: 'auto',
    border: 'none',
    background: 'rgba(0, 0, 0, 0.15)',
    borderRadius: 8,
    width: 40,
    height: 40,
    padding: 0,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    color: '#B0B0B0',
    position: 'relative',
    opacity: 0.5,
    transition: 'opacity 0.15s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },
  ghostBtnActive: {
    opacity: 0.75,
  },
  ghostIcon: {
    filter: 'grayscale(1)',
    display: 'flex',
    transition: 'filter 0.15s ease',
  },
  ghostIconActive: {
    filter: 'grayscale(1) brightness(1.25)',
  },
  // Indicador diminuto: hay opciones (mantener → presets)
  ghostExpandHintDots: {
    position: 'absolute',
    right: 5,
    bottom: 5,
    display: 'flex',
    gap: 2,
    pointerEvents: 'none',
    zIndex: 2,
  },
  ghostExpandDot: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.92)',
    boxShadow: '0 0 2px rgba(0,0,0,0.75)',
  },
  // Acceso rápido Layout (arriba-izquierda, bajo overlays)
  layoutFab: {
    position: 'fixed',
    left: EDGE_SAFE_LEFT,
    top: LAYOUT_FAB_TOP,
    zIndex: 150085,
    pointerEvents: 'auto',
    border: 'none',
    background: 'rgba(0, 0, 0, 0.18)',
    borderRadius: 8,
    width: 40,
    height: 40,
    padding: 0,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    color: '#B0B0B0',
    opacity: 0.52,
    transition: 'opacity 0.15s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },
}

const LONG_PRESS_MS = 420

class ToolsEdgePanel extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      ghostPresetsAnchor: null,
      loadStatsOpen: false,
    }
    this._panelScrollRef = React.createRef()
    this._panelWheelEl = null
    this._detachPanelWheel = null
    this._wwwcLongPressTimer = null
    this._wwwcLongPressFired = false
    this._wwwcPointerId = null
    this._voiceLongPressTimer = null
    this._voiceLongPressFired = false
    this._voicePointerId = null
  }

  componentDidUpdate(prevProps) {
    // Al empezar una carga nueva, colapsar panel de stats para no molestar
    if (
      (this.props.loadSource === 'r2' || this.props.loadSource === 'local') &&
      prevProps.loadSource !== this.props.loadSource &&
      this.state.loadStatsOpen
    ) {
      this.setState({ loadStatsOpen: false })
    }
    this.syncPanelWheel()
  }

  componentDidMount() {
    this.syncPanelWheel()
  }

  componentWillUnmount() {
    this.detachPanelWheel()
    this.clearWwwcLongPress()
    this.clearVoiceLongPress()
  }

  syncPanelWheel = () => {
    const el = this._panelScrollRef.current
    if (!el) {
      this.detachPanelWheel()
      return
    }
    if (el === this._panelWheelEl && this._detachPanelWheel) return
    this.detachPanelWheel()
    this._panelWheelEl = el
    this._detachPanelWheel = attachBoostedWheelScroll(el)
  }

  detachPanelWheel = () => {
    if (typeof this._detachPanelWheel === 'function') {
      this._detachPanelWheel()
      this._detachPanelWheel = null
    }
    this._panelWheelEl = null
  }

  formatMiBps = (v) => {
    if (v == null || !Number.isFinite(v)) return '—'
    if (v < 0.01) return '<0.01'
    return v >= 10 ? v.toFixed(0) : v.toFixed(1)
  }

  formatImgsPerSec = (v) => {
    if (v == null) return '…'
    if (!Number.isFinite(v)) return '—'
    if (v < 0.05) return '<0.1'
    if (v >= 100) return v.toFixed(0)
    if (v >= 10) return v.toFixed(1)
    return v.toFixed(1)
  }

  formatBytes = (b) => {
    if (b == null || !Number.isFinite(b)) return '—'
    const m = b / (1024 * 1024)
    return m >= 100 ? `${m.toFixed(0)} MiB` : `${m.toFixed(1)} MiB`
  }

  formatDurationMs = (ms) => {
    if (ms == null || !Number.isFinite(Number(ms))) return '—'
    const n = Number(ms)
    if (n < 0) return '—'
    if (n < 1000) return `${Math.round(n)} ms`
    const s = n / 1000
    if (s >= 3600) {
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = Math.round(s % 60)
      return `${h}h ${m}m ${sec}s`
    }
    if (s >= 60) {
      return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
    }
    return `${s.toFixed(1)} s`
  }

  toggleLoadStats = () => {
    this.setState((prev) => ({ loadStatsOpen: !prev.loadStatsOpen }))
  }

  renderLoadStatsPanel = () => {
    const progress = this.props.loadProgress
    if (!progress) {
      return (
        <div style={styles.statsPanel}>
          <div style={styles.statsMuted}>Sin estadísticas de descarga.</div>
        </div>
      )
    }
    const fromLocal = isStudyLoadLocal(this.props.loadSource)
    const series = Array.isArray(progress.series) ? progress.series : []
    const offline = progress.offlineCache || null
    const totalTasks =
      typeof progress.totalTasks === 'number' && Number.isFinite(progress.totalTasks)
        ? progress.totalTasks
        : offline && typeof offline.targetCount === 'number'
          ? offline.targetCount
          : null
    const completed =
      typeof progress.completed === 'number' && Number.isFinite(progress.completed)
        ? progress.completed
        : typeof progress.loadedOk === 'number'
          ? progress.loadedOk
          : null
    const loadedOk =
      typeof progress.loadedOk === 'number' && Number.isFinite(progress.loadedOk)
        ? progress.loadedOk
        : completed
    const elapsedMs =
      typeof progress.elapsedMs === 'number' && Number.isFinite(progress.elapsedMs)
        ? progress.elapsedMs
        : null
    const totalBytes =
      typeof progress.totalBytes === 'number' && Number.isFinite(progress.totalBytes)
        ? progress.totalBytes
        : null
    const target =
      offline && offline.targetCount != null
        ? offline.targetCount
        : totalTasks
    const stored =
      offline && offline.completeCount != null ? offline.completeCount : null
    const studyOffline = offline && offline.studyOffline
    const catalogPending = progress.catalogComplete === false
    const fully =
      !catalogPending &&
      (progress.studyFullyLoaded === true ||
        (totalTasks != null &&
          completed != null &&
          completed >= totalTasks &&
          totalTasks > 0))
    const inProgress =
      isStudyLoadActive(this.props.loadSource) && !fully

    return (
      <div style={styles.statsPanel}>
        <div style={styles.statsTitle}>
          {studyLoadSourceDownloadTitle(this.props.loadSource)}{' '}
          {inProgress ? '(en curso)' : fully ? '(completa)' : '(última)'}
        </div>
        {inProgress && typeof this.props.onCancelStudyLoad === 'function' ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              this.props.onCancelStudyLoad()
            }}
            style={{
              marginTop: 8,
              marginBottom: 4,
              width: '100%',
              border: '1px solid rgba(239,154,154,0.45)',
              background: 'rgba(239,154,154,0.12)',
              color: '#ef9a9a',
              borderRadius: 6,
              padding: '6px 8px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancelar descarga
          </button>
        ) : null}
        <div style={styles.statsHighlight}>
          Total: {completed != null ? completed : '—'}/
          {totalTasks != null ? totalTasks : '—'} inst.
          {totalBytes != null && totalBytes > 0
            ? ` · ${this.formatBytes(totalBytes)}`
            : ''}
        </div>
        {showStudyLoadOriginHint() && formatStudyLoadOriginLabel(progress) ? (
          <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
            Origen: {formatStudyLoadOriginLabel(progress)}
            {progress.lanSkipReason
              ? ` · LAN ${progress.lanSkipReason}`
              : ''}
          </div>
        ) : null}
        <div style={styles.statsHighlight}>
          Tiempo: {this.formatDurationMs(elapsedMs)}
          {inProgress && !fully ? ' (transcurrido)' : fully ? ' (total)' : ''}
        </div>
        {!fromLocal ? (
          <>
            <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
              Instant. {this.formatImgsPerSec(progress.instantImgsPerSec)} img/s ·{' '}
              {this.formatMiBps(progress.instantMiBps)} MiB/s
            </div>
            <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
              Media {this.formatImgsPerSec(progress.avgImgsPerSec)} img/s ·{' '}
              {this.formatMiBps(progress.avgMiBps)} MiB/s
              {loadedOk != null ? ` · decod. ${loadedOk}` : ''}
            </div>
          </>
        ) : (
          <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
            Origen: IndexedDB de este equipo
            {loadedOk != null ? ` · decod. ${loadedOk}` : ''}
            {offline && offline.fromCacheHits != null && offline.fromCacheHits > 0
              ? ` · cache ${offline.fromCacheHits}`
              : ''}
          </div>
        )}
        {progress.renderMode || progress.previewLoads != null ? (
          <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
            Modo {progress.renderMode || '—'}
            {progress.previewLoads != null
              ? ` · preview ${progress.previewLoads}`
              : ''}
            {progress.previewFallbacks != null && progress.previewFallbacks > 0
              ? ` · fallback dcm ${progress.previewFallbacks}`
              : ''}
            {progress.dicomLoads != null ? ` · dcm ${progress.dicomLoads}` : ''}
          </div>
        ) : null}
        {offline ? (
          <>
            <Divider style={{ margin: '6px 0', backgroundColor: 'rgba(255,255,255,0.12)' }} />
            {studyOffline ? (
              <div style={{ ...styles.statsLine, ...styles.statsOfflineOk }}>
                Estudio disponible offline
              </div>
            ) : (
              <div style={styles.statsLine}>
                Offline: {stored != null ? stored : '—'}/{target != null ? target : '—'}
                {offline.fromCacheHits != null && offline.fromCacheHits > 0
                  ? ` · cache ${offline.fromCacheHits}`
                  : ''}
              </div>
            )}
            {offline.writeQueueDepth != null && offline.writeQueueDepth > 0 ? (
              <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
                Guardando offline… cola {offline.writeQueueDepth}
              </div>
            ) : null}
            {offline.writesCompleted != null && offline.writesCompleted > 0 ? (
              <div style={{ ...styles.statsLine, ...styles.statsMuted }}>
                Guardados en BD: {offline.writesCompleted}
              </div>
            ) : null}
          </>
        ) : null}
        {series.length > 0 ? (
          <>
            <Divider style={{ margin: '6px 0', backgroundColor: 'rgba(255,255,255,0.12)' }} />
            {series.map((s, i) => {
              const desc = s && s.description != null ? String(s.description) : ''
              const short = desc
                ? ` · ${desc.slice(0, 18)}${desc.length > 18 ? '…' : ''}`
                : ''
              const seriesTime =
                s && s.seriesWallMs != null
                  ? s.seriesWallMs
                  : s && s.completeElapsedMs != null
                    ? s.completeElapsedMs
                    : null
              return (
                <div key={i} style={{ ...styles.statsLine, ...styles.statsMuted }}>
                  S{s && s.seriesNumber != null ? s.seriesNumber : '—'}
                  {short}: {s && s.loaded != null ? s.loaded : '—'}/
                  {s && s.total != null ? s.total : '—'}
                  {seriesTime != null
                    ? ` · ${this.formatDurationMs(seriesTime)}`
                    : ''}
                </div>
              )
            })}
          </>
        ) : null}
        {(this.props.onClearStudyCache || this.props.onClearAllCache) ? (
          <>
            <Divider style={{ margin: '6px 0', backgroundColor: 'rgba(255,255,255,0.12)' }} />
            {this.props.onClearStudyCache ? (
              <button
                type="button"
                style={styles.statsClearBtn}
                onClick={(e) => {
                  e.stopPropagation()
                  if (this.props.onClearStudyCache) this.props.onClearStudyCache()
                }}
              >
                Vaciar caché estudio
              </button>
            ) : null}
            {this.props.onClearAllCache ? (
              <button
                type="button"
                style={styles.statsClearBtn}
                onClick={(e) => {
                  e.stopPropagation()
                  if (this.props.onClearAllCache) this.props.onClearAllCache()
                }}
              >
                Vaciar caché offline
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    )
  }

  renderFooter = () => {
    const {
      loadProgress,
      loadSource,
      gsdfQaPassed,
      onGsdfQa,
      gatewayStatus,
      gatewayCompatOk,
      onGatewayOpen,
      onGateway,
      portalChrome = null,
      showPortalDisclaimerRail = false,
    } = this.props
    const showSec = (id) => portalSectionVisible(portalChrome, id)
    const hasStats = !!loadProgress
    const fullyLoaded =
      loadProgress &&
      loadProgress.catalogComplete !== false &&
      (loadProgress.studyFullyLoaded === true ||
        (typeof loadProgress.totalTasks === 'number' &&
          loadProgress.totalTasks > 0 &&
          typeof loadProgress.completed === 'number' &&
          loadProgress.completed >= loadProgress.totalTasks))
    const loading =
      isStudyLoadActive(loadSource) && !fullyLoaded
    const fromLocal = isStudyLoadLocal(loadSource)
    const { loadStatsOpen } = this.state
    const infoColor =
      loadStatsOpen || loading
        ? styles.infoBtnActive.color
        : styles.infoBtn.color
    const qaOk = !!gsdfQaPassed
    const openGateway = () => {
      if (typeof onGatewayOpen === 'function') onGatewayOpen()
      else if (typeof onGateway === 'function') onGateway()
    }
    const showGatewayBadge = showSec('gatewayBadge')
    const showQa = showSec('qa') && !!onGsdfQa

    return (
      <div style={styles.footerRow}>
        <Collapse in={loadStatsOpen} timeout="auto" unmountOnExit style={{ width: '100%' }}>
          {this.renderLoadStatsPanel()}
        </Collapse>
        <div style={styles.footerMeta}>
          {showPortalDisclaimerRail ? <PortalDisclaimerRail /> : null}
          {showGatewayBadge ? (
          <GatewayStatusBadge
            status={gatewayStatus || 'local_only'}
            compatOk={gatewayCompatOk !== false}
            onClick={openGateway}
            style={styles.gatewayBadge}
          />
          ) : null}
          {showQa ? (
            <Tooltip
              title={qaOk ? 'QA Screen: OK — tocar para repetir' : 'QA Screen: Unverified — tocar para validar'}
              placement="left"
            >
              <button
                type="button"
                style={{
                  ...styles.qaBadge,
                  ...(qaOk ? styles.qaBadgeOk : styles.qaBadgeUnverified),
                }}
                onClick={() => onGsdfQa()}
                aria-label={qaOk ? 'QA Screen: OK' : 'QA Screen: Unverified'}
              >
                {qaOk ? 'QA: OK' : 'QA: ?'}
              </button>
            </Tooltip>
          ) : null}
          {loading ? (
            <CircularProgress size={12} thickness={5} style={{ color: '#9e9e9e', marginRight: 2 }} />
          ) : null}
          <span style={styles.versionText} aria-label={`Versión ${packageJson.version}`}>
            v{packageJson.version}
          </span>
          {hasStats || loading ? (
            <Tooltip
              title={
                loadStatsOpen
                  ? fromLocal
                    ? 'Ocultar carga local'
                    : 'Ocultar descarga'
                  : fromLocal
                    ? 'Estadísticas de carga local'
                    : 'Estadísticas de descarga'
              }
              placement="left"
            >
              <IconButton
                size="small"
                style={{ ...styles.infoBtn, color: infoColor }}
                onClick={this.toggleLoadStats}
                aria-label={fromLocal ? 'Estadísticas de carga local' : 'Estadísticas de descarga'}
                aria-expanded={loadStatsOpen}
              >
                <Icon path={mdiInformationOutline} size={0.65} color={infoColor} />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
    )
  }

  clearWwwcLongPress = () => {
    if (this._wwwcLongPressTimer) {
      clearTimeout(this._wwwcLongPressTimer)
      this._wwwcLongPressTimer = null
    }
  }

  clearVoiceLongPress = () => {
    if (this._voiceLongPressTimer) {
      clearTimeout(this._voiceLongPressTimer)
      this._voiceLongPressTimer = null
    }
  }

  openVoicePanel = () => {
    const open = this.props.onVoiceNoteOpenPanel
    if (typeof open !== 'function') return
    this._voiceLongPressFired = true
    open()
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(12)
      } catch (err) {
        /* ignore */
      }
    }
  }

  handleVoicePointerDown = (e) => {
    if (e.button != null && e.button !== 0) return
    if (this.props.voiceNoteBusy || !this.props.anyOpen) return
    this._voiceLongPressFired = false
    this._voicePointerId = e.pointerId
    this.clearVoiceLongPress()
    this._voiceLongPressTimer = setTimeout(() => {
      this._voiceLongPressTimer = null
      this.openVoicePanel()
    }, LONG_PRESS_MS)
  }

  handleVoicePointerUp = (e) => {
    this.clearVoiceLongPress()
    if (this._voicePointerId != null && e.pointerId !== this._voicePointerId) return
    this._voicePointerId = null
  }

  handleVoicePointerCancel = () => {
    this.clearVoiceLongPress()
    this._voicePointerId = null
  }

  handleVoiceClick = (e) => {
    if (this._voiceLongPressFired) {
      e.preventDefault()
      e.stopPropagation()
      this._voiceLongPressFired = false
      return
    }
    if (this.props.voiceNoteBusy || !this.props.anyOpen) return
    if (this.props.onVoiceNoteToggle) this.props.onVoiceNoteToggle()
  }

  handleVoiceContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.clearVoiceLongPress()
    if (this.props.voiceNoteBusy || !this.props.anyOpen) return
    this.openVoicePanel()
  }

  renderVoiceNoteButton = () => {
    const recording = !!this.props.voiceNoteRecording
    const busy = !!this.props.voiceNoteBusy
    const blocked = !this.props.anyOpen || busy
    const tip = recording
      ? 'Click: detener · mantener / clic derecho = panel'
      : 'Click: grabar · mantener / clic derecho = panel'
    // Botón nativo (como WW/WC ghost): IconButton de MUI a veces no dispara contextmenu.
    return (
      <Tooltip title={tip} placement="left">
        <span
          style={{
            display: 'flex',
            width: '100%',
            minWidth: 0,
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            disabled={blocked && !recording}
            aria-label={tip}
            aria-pressed={recording}
            aria-haspopup="dialog"
            title={tip}
            onClick={this.handleVoiceClick}
            onPointerDown={this.handleVoicePointerDown}
            onPointerUp={this.handleVoicePointerUp}
            onPointerLeave={this.handleVoicePointerCancel}
            onPointerCancel={this.handleVoicePointerCancel}
            onContextMenu={this.handleVoiceContextMenu}
            style={{
              ...styles.btn,
              position: 'relative',
              margin: 0,
              border: 'none',
              background: recording ? 'rgba(229, 57, 53, 0.12)' : 'transparent',
              cursor: blocked && !recording ? 'not-allowed' : 'pointer',
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              color: recording ? '#e53935' : GROUP.image,
              opacity: blocked && !recording ? 0.35 : 1,
              ...(recording ? styles.activeRing : null),
            }}
          >
            <Icon
              path={recording ? mdiMicrophoneOff : mdiMicrophone}
              size={iconSize}
              color={recording ? '#e53935' : GROUP.image}
            />
            {this.renderGhostExpandHint()}
          </button>
        </span>
      </Tooltip>
    )
  }

  toolColor = (groupColor, isActive) => {
    if (isActive) return '#FFFFFF'
    return groupColor
  }

  renderToolButton = ({ title, tool, path, color, onClick, isActive, disabled }) => {
    const active = typeof isActive === 'boolean' ? isActive : this.props.toolActive === tool
    // PREVIEW: tools pixel-accurate hacen upgrade on-demand (no se bloquean en UI)
    const isDisabled = !!disabled
    return (
      <Tooltip title={title} key={tool || title} placement="left">
        <span style={{ display: 'flex', width: '100%', minWidth: 0 }}>
          <IconButton
            style={{
              ...styles.btn,
              color,
              opacity: isDisabled ? 0.35 : 1,
              ...(active && !isDisabled ? styles.activeRing : null),
            }}
            onClick={
              isDisabled
                ? (e) => {
                    e.preventDefault()
                  }
                : onClick
            }
          >
            <Icon path={path} size={iconSize} color={this.toolColor(color, active && !isDisabled)} />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  renderTextChip = ({ title, label, onClick, isActive }) => (
    <Tooltip title={title} key={title} placement="left">
      <button
        type="button"
        onClick={onClick}
        style={{
          ...styles.btn,
          minWidth: 40,
          fontSize: 11,
          fontWeight: 700,
          color: '#fff',
          background: isActive ? 'rgba(129,199,132,0.28)' : 'transparent',
          border: isActive ? '1px solid #81C784' : '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    </Tooltip>
  )

  renderGroup = (label, color, children) => (
    <div key={label}>
      <div style={{ ...styles.groupTitle, color }}>{label}</div>
      <div style={styles.grid}>{children}</div>
    </div>
  )

  /**
   * Toggle de 2 estados: Preview (PREVIEW) / Diagnóstico.
   * Ubicar debajo de ROI en el drawer edge.
   */
  renderRenderModeToggle = () => {
    if (!this.props.onToggleRenderMode && !this.props.onSetRenderMode) return null
    const isPreview = this.props.renderMode === RENDER_MODE.PREVIEW
    const isDiagnostic = !isPreview

    const setMode = (mode) => {
      if (typeof this.props.onSetRenderMode === 'function') {
        this.props.onSetRenderMode(mode)
        return
      }
      if (mode === this.props.renderMode) return
      if (this.props.onToggleRenderMode) this.props.onToggleRenderMode()
    }

    return (
      <div style={styles.modeToggleWrap} role="group" aria-label="Modo de render">
        <div style={styles.modeToggleLabel}>Modo</div>
        <div style={styles.modeToggleTrack} role="radiogroup" aria-label="Modo de visualización">
          <Tooltip title={t('renderMode.diagnosticTooltip')} placement="left">
            <button
              type="button"
              role="radio"
              aria-checked={isDiagnostic}
              aria-label={t('renderMode.diagnostic')}
              onClick={() => setMode(RENDER_MODE.DIAGNOSTIC)}
              style={{
                ...styles.modeToggleOption,
                ...(isDiagnostic ? styles.modeToggleOptionActive : null),
              }}
            >
              <Icon
                path={mdiHospitalBox}
                size={0.72}
                color={isDiagnostic ? '#fff' : 'rgba(255,255,255,0.5)'}
              />
              {t('renderMode.diagnostic')}
            </button>
          </Tooltip>
          <Tooltip title={t('renderMode.previewTooltip')} placement="left">
            <button
              type="button"
              role="radio"
              aria-checked={isPreview}
              aria-label={t('renderMode.preview')}
              onClick={() => setMode(RENDER_MODE.PREVIEW)}
              style={{
                ...styles.modeToggleOption,
                ...styles.modeToggleOptionLast,
                ...(isPreview ? styles.modeToggleOptionActivePreview : null),
              }}
            >
              <Icon
                path={mdiImageOutline}
                size={0.72}
                color={isPreview ? '#FFE0B2' : 'rgba(255,255,255,0.5)'}
              />
              {t('renderMode.preview')}
            </button>
          </Tooltip>
        </div>
      </div>
    )
  }

  handleModeTool = (tool) => {
    if (this.props.toolExecute) this.props.toolExecute(tool)
    if (this.props.onCloseAfterTool) this.props.onCloseAfterTool()
  }

  handleToggleTool = (tool) => {
    if (this.props.toolExecute) this.props.toolExecute(tool)
  }

  handleWwwcPointerDown = (e) => {
    this._wwwcLongPressFired = false
    this._wwwcPointerId = e.pointerId
    this.clearWwwcLongPress()
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    const target = e.currentTarget
    this._wwwcLongPressTimer = setTimeout(() => {
      this._wwwcLongPressFired = true
      this.setState({ ghostPresetsAnchor: target })
      // Haptics suaves en mobile si existe
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try { navigator.vibrate(12) } catch (err2) { /* ignore */ }
      }
    }, LONG_PRESS_MS)
  }

  handleWwwcPointerUp = (e) => {
    this.clearWwwcLongPress()
    if (this._wwwcPointerId != null && e.pointerId !== this._wwwcPointerId) return
    if (this._wwwcLongPressFired) {
      e.preventDefault()
      e.stopPropagation()
    }
    this._wwwcPointerId = null
  }

  handleWwwcPointerCancel = () => {
    this.clearWwwcLongPress()
    this._wwwcPointerId = null
  }

  handleWwwcClick = (e) => {
    // Evita activar la tool si se acaba de abrir el menú por long-press
    if (this._wwwcLongPressFired) {
      e.preventDefault()
      e.stopPropagation()
      this._wwwcLongPressFired = false
      return
    }
    if (this.props.toolExecute) this.props.toolExecute('Wwwc')
  }

  handleWwwcContextMenu = (e) => {
    // En touch el long-press a menudo lanza contextmenu; usamos ese gesto para presets
    e.preventDefault()
  }

  handleCloseGhostPresets = () => {
    this.setState({ ghostPresetsAnchor: null })
  }

  renderGhostExpandHint = () => (
    <span style={styles.ghostExpandHintDots} aria-hidden>
      <span style={styles.ghostExpandDot} />
      <span style={styles.ghostExpandDot} />
      <span style={styles.ghostExpandDot} />
    </span>
  )

  renderLayoutFab = () => {
    const { layoutPinned, onLayoutOpen } = this.props
    if (!layoutPinned || !onLayoutOpen) return null
    return (
      <Tooltip title="Layout" placement="right">
        <button
          type="button"
          aria-label="Layout"
          data-udv-layout-fab="1"
          onClick={(e) => onLayoutOpen(e)}
          style={styles.layoutFab}
        >
          <span style={styles.ghostIcon}>
            <Icon path={mdiViewGridPlusOutline} size={1.05} color="#C8C8C8" />
          </span>
        </button>
      </Tooltip>
    )
  }

  renderGhostQuickIcons = () => {
    const { toolActive, toolExecute, onPresetSelect, currentModality, showPresets } =
      this.props
    const { ghostPresetsAnchor } = this.state
    // Menú de presets (long-press); hideTrigger en WWPresetManager
    const canPresets = !!showPresets && !!onPresetSelect
    const wwwcActive = toolActive === 'Wwwc'
    const notoolActive = toolActive === 'notool'
    const isPreview = this.props.renderMode === RENDER_MODE.PREVIEW
    const modeTitle = isPreview
      ? t('renderMode.ghostToDiagnostic')
      : t('renderMode.ghostToPreview')
    const modeActive = isPreview

    return (
      <div style={styles.ghostRail} aria-label="Herramientas rápidas">
        <Tooltip
          title={canPresets ? 'WW/WC · mantener para presets' : 'WW/WC'}
          placement="left"
        >
          <button
            type="button"
            aria-label="WW/WC"
            aria-pressed={wwwcActive}
            aria-haspopup={canPresets ? 'menu' : undefined}
            onClick={this.handleWwwcClick}
            onPointerDown={canPresets ? this.handleWwwcPointerDown : undefined}
            onPointerUp={canPresets ? this.handleWwwcPointerUp : undefined}
            onPointerLeave={canPresets ? this.handleWwwcPointerCancel : undefined}
            onPointerCancel={canPresets ? this.handleWwwcPointerCancel : undefined}
            onContextMenu={canPresets ? this.handleWwwcContextMenu : undefined}
            style={{
              ...styles.ghostBtn,
              ...(wwwcActive ? styles.ghostBtnActive : null),
            }}
          >
            <span style={{
              ...styles.ghostIcon,
              ...(wwwcActive ? styles.ghostIconActive : null),
            }}>
              <Icon path={mdiWhiteBalanceSunny} size={1.05} color="#C8C8C8" />
            </span>
            {canPresets ? this.renderGhostExpandHint() : null}
          </button>
        </Tooltip>

        <Tooltip title="No Tool" placement="left">
          <button
            type="button"
            aria-label="No Tool"
            aria-pressed={notoolActive}
            onClick={() => toolExecute && toolExecute('notool')}
            style={{
              ...styles.ghostBtn,
              ...(notoolActive ? styles.ghostBtnActive : null),
            }}
          >
            <span style={{
              ...styles.ghostIcon,
              ...(notoolActive ? styles.ghostIconActive : null),
            }}>
              <Icon path={mdiCursorDefault} size={1.05} color="#C8C8C8" />
            </span>
          </button>
        </Tooltip>

        {this.props.onToggleRenderMode ? (
          <Tooltip title={modeTitle} placement="left">
            <button
              type="button"
              aria-label={modeTitle}
              aria-pressed={modeActive}
              onClick={() => {
                if (this.props.onToggleRenderMode) this.props.onToggleRenderMode()
              }}
              style={{
                ...styles.ghostBtn,
                ...(modeActive ? styles.ghostBtnActive : null),
              }}
            >
              <span
                style={{
                  ...styles.ghostIcon,
                  ...(modeActive ? styles.ghostIconActive : null),
                }}
              >
                <Icon
                  path={isPreview ? mdiImageOutline : mdiHospitalBox}
                  size={1.05}
                  color="#C8C8C8"
                />
              </span>
            </button>
          </Tooltip>
        ) : null}

        {canPresets && (
          <WWPresetManager
            hideTrigger
            skin="edge"
            currentModality={currentModality}
            onPresetSelect={onPresetSelect}
            anchorEl={ghostPresetsAnchor}
            open={Boolean(ghostPresetsAnchor)}
            onClose={this.handleCloseGhostPresets}
          />
        )}
      </div>
    )
  }

  render() {
    const {
      open,
      onToggle,
      onClose,
      docked,
      seriesLink,
      seriesLinkWwwc,
      referenceLines,
      showReferenceLines = true,
      hideReferenceLinesOnActive = false,
      anyOpen,
      showCine,
      showOrthanc,
      onOrthanc,
      showGateway,
      gatewayConnected,
      onGateway,
      showLocalStudies,
      onLocalStudies,
      showRelatedStudies,
      onRelatedStudies,
      showWorklist,
      onWorklist,
      compareMode,
      compareModeEnabled,
      onToggleCompareMode,
      isFullscreen,
      showPresets,
      currentModality,
      onPresetSelect,
      showDsa,
      dsaEnabled,
      dsaMaskMode,
      dsaFrameCount,
      dsaPixelShift,
      dsaInvert,
      dsaGain,
      dsaOffset,
      dsaNoiseFloor,
      dsaEngineMode,
      dsaSynthMode,
      dsaRoadmap,
      onDsaSelect,
      onLayout,
      onLayoutToggle,
      layoutPinned,
      onFullscreen,
      onPwaExit,
      onInstallPwa,
      pwaStandalone,
      onReset,
      onExplorer,
      onCine,
      portalChrome = null,
    } = this.props

    const isDocked = !!docked
    const showSec = (id) => portalSectionVisible(portalChrome, id)

    const edgeChrome =
      !isDocked &&
      typeof document !== 'undefined' &&
      document.body
        ? createPortal(
            <>
              <button
                type="button"
                style={styles.tabHit}
                aria-label={open ? 'Cerrar herramientas' : 'Abrir herramientas'}
                onClick={onToggle}
              >
                <span style={styles.tabFace}>
                  <Icon path={mdiTools} size={0.75} color="#fff" />
                  <Icon path={open ? mdiChevronRight : mdiChevronLeft} size={0.7} color="#fff" />
                </span>
              </button>
              {/* Layout pin: siempre visible (si activo), panel abierto o cerrado */}
              {this.renderLayoutFab()}
              {/* Ghost rail: always visible (modo findable; no solo drawer) */}
              {this.renderGhostQuickIcons()}
            </>,
            document.body
          )
        : null

    return (
      <>
        {edgeChrome}

        <Drawer
          anchor="right"
          variant={isDocked ? 'permanent' : 'temporary'}
          open={isDocked ? true : !!open}
          onClose={isDocked ? undefined : onClose}
          ModalProps={isDocked ? undefined : { keepMounted: true }}
          PaperProps={{
            ref: this._panelScrollRef,
            style: isDocked ? styles.dockedPaper : styles.paper,
          }}
          style={isDocked ? { position: 'relative', zIndex: 1, height: '100%' } : { zIndex: 150120 }}
          className={isDocked ? 'udv-studio-tools-drawer' : undefined}
        >
          {showSec('sistema')
            ? this.renderGroup(
            'Sistema',
            GROUP.system,
            <>
              {this.renderToolButton({
                title: layoutPinned
                  ? 'Layout (activo en pantalla · tocar para ocultar)'
                  : 'Layout (mostrar acceso rápido)',
                tool: 'layout',
                path: mdiViewGridPlusOutline,
                color: GROUP.system,
                onClick: (e) => {
                  if (onLayoutToggle) onLayoutToggle(e)
                  else if (onLayout) onLayout(e)
                },
                isActive: !!layoutPinned,
              })}
              {this.renderToolButton({
                title: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
                tool: 'fullscreen',
                path: isFullscreen ? mdiFullscreenExit : mdiFullscreen,
                color: GROUP.system,
                onClick: () => {
                  if (onFullscreen) onFullscreen()
                },
                isActive: !!isFullscreen,
              })}
              {!pwaStandalone &&
                onInstallPwa &&
                this.renderToolButton({
                  title: getPwaInstallLabel(t),
                  tool: 'pwa-install',
                  path: mdiPlusBox,
                  color: GROUP.system,
                  onClick: () => {
                    if (onInstallPwa) onInstallPwa()
                  },
                  isActive: false,
                })}
              {pwaStandalone &&
                this.renderToolButton({
                  title: 'Salir de UDV',
                  tool: 'pwa-exit',
                  path: mdiExitToApp,
                  color: GROUP.system,
                  onClick: () => {
                    if (onPwaExit) onPwaExit()
                  },
                  isActive: false,
                })}
              {this.props.swUpdatePending &&
                this.renderToolButton({
                  title: 'Avisos / notificaciones',
                  tool: 'sw-update',
                  path: mdiBell,
                  color: GROUP.system,
                  onClick: () => {
                    if (this.props.onOpenSwUpdate) this.props.onOpenSwUpdate()
                  },
                  isActive: true,
                })}
              {anyOpen &&
                this.renderToolButton({
                  title: 'Reset Image',
                  tool: 'reset',
                  path: mdiRefresh,
                  color: GROUP.system,
                  onClick: (e) => {
                    if (e && e.preventDefault) e.preventDefault()
                    if (e && e.stopPropagation) e.stopPropagation()
                    if (onReset) onReset()
                  },
                  isActive: false,
                })}
              {anyOpen &&
                this.renderToolButton({
                  title: 'Explorer',
                  tool: 'explorer',
                  path: mdiAnimationOutline,
                  color: GROUP.system,
                  onClick: () => {
                    if (onExplorer) onExplorer()
                  },
                  isActive: false,
                })}
              {showOrthanc &&
                this.renderToolButton({
                  title: 'Connect to Orthanc',
                  tool: 'orthanc',
                  path: mdiDatabase,
                  color: GROUP.system,
                  onClick: () => {
                    if (onOrthanc) onOrthanc()
                  },
                  isActive: false,
                })}
              {showGateway &&
                this.renderToolButton({
                  title: gatewayConnected
                    ? 'TJS Gateway (conectado)'
                    : 'TJS Gateway / Iniciar sesión',
                  tool: 'gateway',
                  path: gatewayConnected ? mdiCloudCheck : mdiCloudLock,
                  color: GROUP.system,
                  onClick: () => {
                    if (onGateway) onGateway()
                  },
                  isActive: !!gatewayConnected,
                })}
              {!showGateway &&
                this.renderToolButton({
                  title:
                    'Inicio de sesión profesional (solo con invite de clínica)',
                  tool: 'gateway',
                  path: mdiCloudLock,
                  color: GROUP.system,
                  onClick: () => {},
                  isActive: false,
                  disabled: true,
                })}
              {showLocalStudies &&
                this.renderToolButton({
                  title: 'Estudios en storage local',
                  tool: 'local-studies',
                  path: mdiInboxArrowDown,
                  color: GROUP.system,
                  onClick: () => {
                    if (onLocalStudies) onLocalStudies()
                  },
                  isActive: false,
                })}
              {anyOpen &&
                (this.props.studyDownloadUrl ||
                  this.props.localStudyZipEligible) &&
                this.renderToolButton({
                  title:
                    this.props.studyZipDownloadTitle ||
                    'Descargar estudio (.zip)',
                  tool: 'downloadStudy',
                  path: mdiDownload,
                  color: GROUP.system,
                  onClick: () => {
                    if (this.props.onDownloadStudy) this.props.onDownloadStudy()
                  },
                  isActive: false,
                  disabled:
                    !!this.props.studyZipDownloadBusy ||
                    !(
                      this.props.studyDownloadUrl ||
                      this.props.localStudyZipReady
                    ),
                })}
            </>
          )
            : null}

          {showSec('nav')
            ? this.renderGroup(
            'Navegación',
            GROUP.navigation,
            <>
              {this.renderToolButton({
                title: 'No Tool',
                tool: 'notool',
                path: mdiCursorDefault,
                color: GROUP.navigation,
                onClick: () => this.handleModeTool('notool'),
              })}
              {showReferenceLines
                ? this.renderToolButton({
                    title: 'Reference Lines',
                    tool: 'referencelines',
                    path: mdiArrowSplitHorizontal,
                    color: GROUP.navigation,
                    onClick: () => this.handleToggleTool('referencelines'),
                    isActive: !!referenceLines,
                    disabled: !anyOpen,
                  })
                : null}
              {showReferenceLines && referenceLines
                ? this.renderToolButton({
                    title: 'Ocultar línea en viewport activo',
                    tool: 'referencelinesactivehide',
                    path: mdiEyeOff,
                    color: GROUP.navigation,
                    onClick: () => this.handleToggleTool('referencelinesactivehide'),
                    isActive: !!hideReferenceLinesOnActive,
                    disabled: !anyOpen,
                  })
                : null}
              {this.renderToolButton({
                title: 'Link Series',
                tool: 'serieslink',
                path: mdiVectorLink,
                color: GROUP.navigation,
                onClick: () => this.handleToggleTool('serieslink'),
                isActive: !!seriesLink,
                disabled: !!this.props.mprActive,
              })}
              {seriesLink || this.props.mprActive
                ? this.renderToolButton({
                    title: this.props.mprActive
                      ? 'Link WW/WL (planos MPR)'
                      : 'Link WW/WL',
                    tool: 'serieslinkwwwc',
                    path: mdiWhiteBalanceSunny,
                    color: GROUP.navigation,
                    onClick: () => this.handleToggleTool('serieslinkwwwc'),
                    isActive: !!seriesLinkWwwc,
                  })
                : null}
            </>
          )
            : null}

          {anyOpen && showSec('pet')
            ? this.renderGroup(
                'Fusión PET',
                GROUP.image,
                <>
                  {this.renderToolButton({
                    title: this.props.petFusionAvailable
                      ? 'Superponer PET sobre CT/MR'
                      : 'No hay serie PET en el estudio',
                    tool: 'petFusion',
                    path: mdiLayers,
                    color: GROUP.image,
                    onClick: () => {
                      if (this.props.onPetFusionToggle) this.props.onPetFusionToggle()
                    },
                    isActive: !!this.props.petFusionOn,
                    disabled: !this.props.petFusionAvailable && !this.props.petFusionOn,
                  })}
                  {this.props.petFusionOn
                    ? PET_CLUT_IDS.map((id) =>
                        this.renderTextChip({
                          title: PET_CLUT_LABELS[id],
                          label: id === 'hotMetal' ? 'HM' : id === 'rainbow' ? 'RB' : 'Fire',
                          onClick: () => {
                            if (this.props.onPetFusionClut) this.props.onPetFusionClut(id)
                          },
                          isActive: (this.props.petFusionClut || 'hotMetal') === id,
                        })
                      )
                    : null}
                  {this.props.petFusionOn
                    ? [25, 50, 75, 100].map((pct) =>
                        this.renderTextChip({
                          title: `Opacidad ${pct}%`,
                          label: `${pct}`,
                          onClick: () => {
                            if (this.props.onPetFusionOpacity) this.props.onPetFusionOpacity(pct)
                          },
                          isActive: Number(this.props.petFusionOpacity) === pct,
                        })
                      )
                    : null}
                  {this.props.petFusionOn
                    ? [5, 10, 15].map((mx) =>
                        this.renderTextChip({
                          title: `Ventana SUV 0–${mx}`,
                          label: `SUV${mx}`,
                          onClick: () => {
                            if (this.props.onPetFusionWindowMax) this.props.onPetFusionWindowMax(mx)
                          },
                          isActive: Number(this.props.petFusionWindowMax) === mx,
                        })
                      )
                    : null}
                </>
              )
            : null}

          {anyOpen && showSec('gsps')
            ? this.renderGroup(
                'GSPS / SR',
                GROUP.measure,
                <>
                  {this.renderToolButton({
                    title: this.props.gspsAvailable
                      ? this.props.gspsOn
                        ? 'Ocultar GSPS importado'
                        : 'Aplicar GSPS sobre el SOP referenciado'
                      : 'No hay serie PR (GSPS) en el estudio',
                    tool: 'gspsOverlay',
                    path: mdiImageOutline,
                    color: GROUP.measure,
                    onClick: () => {
                      if (this.props.onGspsToggle) this.props.onGspsToggle()
                    },
                    isActive: !!this.props.gspsOn && !!this.props.gspsAvailable,
                    disabled: !this.props.gspsAvailable && !this.props.gspsOn,
                  })}
                  {this.renderToolButton({
                    title: 'Exportar mediciones a GSPS (.dcm)',
                    tool: 'gspsExport',
                    path: mdiFileCad,
                    color: GROUP.measure,
                    onClick: () => {
                      if (this.props.onGspsExport) this.props.onGspsExport()
                    },
                  })}
                  {this.renderToolButton({
                    title: 'Exportar informe SR (.dcm)',
                    tool: 'srExport',
                    path: mdiFileDocument,
                    color: GROUP.measure,
                    onClick: () => {
                      if (this.props.onSrExport) this.props.onSrExport()
                    },
                  })}
                  {this.renderToolButton({
                    title: 'Adjuntar PDF / informe (DOC)',
                    tool: 'pdfAttach',
                    path: mdiFileCad,
                    color: GROUP.measure,
                    onClick: () => {
                      if (this.props.onAttachPdf) this.props.onAttachPdf()
                    },
                  })}
                </>
              )
            : null}

          {anyOpen && this.props.onMpr && showSec('mpr')
            ? this.renderGroup(
                'MPR',
                GROUP.navigation,
                <>
                  {this.props.mprActive
                    ? this.renderToolButton({
                        title: 'Salir de MPR',
                        tool: 'mprExit',
                        path: mdiRefresh,
                        color: GROUP.navigation,
                        onClick: () => {
                          if (this.props.onMprExit) this.props.onMprExit()
                        },
                      })
                    : null}
                  {this.props.mprOrthogonalAllowed
                    ? this.renderToolButton({
                        title: this.props.mprAllowed
                          ? 'MPR Orthogonal'
                          : this.props.mprMessage || 'MPR no disponible',
                        tool: 'mprOrthogonal',
                        path: mdiViewColumn,
                        color: GROUP.navigation,
                        onClick: () => this.props.onMpr('orthogonal'),
                        isActive: !!this.props.visibleMprOrthogonal,
                        disabled: !this.props.mprAllowed,
                      })
                    : null}
                  {this.renderToolButton({
                    title: this.props.mprAllowed
                      ? 'MPR Coronal'
                      : this.props.mprMessage || 'MPR no disponible',
                    tool: 'mprCoronal',
                    path: mdiAlphaCBox,
                    color: GROUP.navigation,
                    onClick: () => this.props.onMpr('coronal'),
                    isActive: !!this.props.visibleMprCoronal,
                    disabled: !this.props.mprAllowed,
                  })}
                  {this.renderToolButton({
                    title: this.props.mprAllowed
                      ? 'MPR Sagittal'
                      : this.props.mprMessage || 'MPR no disponible',
                    tool: 'mprSagittal',
                    path: mdiAlphaSBox,
                    color: GROUP.navigation,
                    onClick: () => this.props.onMpr('sagittal'),
                    isActive: !!this.props.visibleMprSagittal,
                    disabled: !this.props.mprAllowed,
                  })}
                  {this.renderToolButton({
                    title: this.props.mprAllowed
                      ? 'MPR Axial'
                      : this.props.mprMessage || 'MPR no disponible',
                    tool: 'mprAxial',
                    path: mdiAlphaABox,
                    color: GROUP.navigation,
                    onClick: () => this.props.onMpr('axial'),
                    isActive: !!this.props.visibleMprAxial,
                    disabled: !this.props.mprAllowed,
                  })}
                  {this.props.mprActive
                    ? this.renderToolButton({
                        title: 'Punto mira',
                        tool: 'mprCrosshair',
                        path: mdiCrosshairsGps,
                        color: GROUP.navigation,
                        onClick: () => {
                          if (this.props.onMprCrosshairToggle) {
                            this.props.onMprCrosshairToggle()
                          }
                        },
                        isActive: !!this.props.mprCrosshairActive,
                      })
                    : null}
                  {this.props.mprActive && this.props.onMprSlabMode
                    ? this.renderTextChip({
                        title: 'Corte fino (1 vóxel)',
                        label: 'Fino',
                        onClick: () => {
                          if (this.props.onMprSlabMode) this.props.onMprSlabMode('thin')
                        },
                        isActive: (this.props.mprSlabMode || 'thin') === 'thin',
                      })
                    : null}
                  {this.props.mprActive && this.props.onMprSlabMode
                    ? this.renderTextChip({
                        title: 'MIP — máxima intensidad',
                        label: 'MIP',
                        onClick: () => {
                          if (this.props.onMprSlabMode) this.props.onMprSlabMode('mip')
                        },
                        isActive: this.props.mprSlabMode === 'mip',
                      })
                    : null}
                  {this.props.mprActive && this.props.onMprSlabMode
                    ? this.renderTextChip({
                        title: 'MinIP — mínima intensidad',
                        label: 'MinIP',
                        onClick: () => {
                          if (this.props.onMprSlabMode) this.props.onMprSlabMode('minip')
                        },
                        isActive: this.props.mprSlabMode === 'minip',
                      })
                    : null}
                  {this.props.mprActive && this.props.onMprSlabMode
                    ? this.renderTextChip({
                        title: 'Promedio HU',
                        label: 'Avg',
                        onClick: () => {
                          if (this.props.onMprSlabMode) this.props.onMprSlabMode('avg')
                        },
                        isActive: this.props.mprSlabMode === 'avg',
                      })
                    : null}
                  {this.props.mprActive && this.props.onMprSlabMode
                    ? [0, 3, 5, 10].map((mm) =>
                        this.renderTextChip({
                          title: mm === 0 ? 'Sin grosor' : `Grosor ${mm} mm`,
                          label: mm === 0 ? 'Off' : `${mm}`,
                          onClick: () => {
                            if (this.props.onMprSlabMm) this.props.onMprSlabMm(mm)
                          },
                          isActive: Number(this.props.mprSlabMm) === mm,
                        })
                      )
                    : null}
                  {this.props.mprActive
                    ? this.renderToolButton({
                        title: this.props.mpr3dAllowed
                          ? (this.props.visibleMpr3D ? 'Volver a MPR' : '3D')
                          : this.props.mpr3dMessage || '3D no disponible',
                        tool: 'mpr3d',
                        path: mdiCubeOutline,
                        color: GROUP.navigation,
                        onClick: () => {
                          if (this.props.onMpr3d) this.props.onMpr3d()
                        },
                        isActive: !!this.props.visibleMpr3D,
                        disabled: !this.props.mpr3dAllowed,
                      })
                    : null}
                  {this.props.visibleMpr3D
                    ? this.renderToolButton({
                        title: this.props.mpr3dDecapado
                          ? 'Volver al volumen completo'
                          : 'Decapar según planos MPR',
                        tool: 'mpr3dDecapado',
                        path: mdiContentCut,
                        color: GROUP.navigation,
                        onClick: () => {
                          if (this.props.onMpr3dDecapado) this.props.onMpr3dDecapado()
                        },
                        isActive: !!this.props.mpr3dDecapado,
                      })
                    : null}
                  {this.props.mprActive && this.props.scoutAvailable
                    ? this.renderTextChip({
                        title: 'Scout Sync — líneas de referencia',
                        label: 'Scout',
                        onClick: () => {
                          if (this.props.onScoutSyncToggle) this.props.onScoutSyncToggle()
                        },
                        isActive: !!this.props.scoutSyncEnabled,
                      })
                    : null}
                  {this.props.mprActive && this.props.scoutAvailable
                    ? this.renderTextChip({
                        title: 'Panel Scout flotante',
                        label: 'Panel',
                        onClick: () => {
                          if (this.props.onScoutPanelToggle) this.props.onScoutPanelToggle()
                        },
                        isActive: !!this.props.scoutPanelOpen,
                      })
                    : null}
                </>
              )
            : null}

          {showSec('imagen')
            ? this.renderGroup(
            'Imagen',
            GROUP.image,
            <>
              {showCine &&
                this.renderToolButton({
                  title: 'Cine Player',
                  tool: 'cine',
                  path: mdiVideo,
                  color: GROUP.image,
                  onClick: () => {
                    if (onCine) onCine()
                  },
                  isActive: !!this.props.cineActive,
                })}
              {this.renderToolButton({
                title: 'WW/WC',
                tool: 'Wwwc',
                path: mdiWhiteBalanceSunny,
                color: GROUP.image,
                onClick: () => this.handleModeTool('Wwwc'),
              })}
              {showPresets && onPresetSelect ? (
                <WWPresetManager
                  skin="edge"
                  placement="left"
                  currentModality={currentModality}
                  onPresetSelect={onPresetSelect}
                />
              ) : null}
              {this.renderToolButton({
                title: 'Invertir',
                tool: 'Invert',
                path: mdiInvertColors,
                color: GROUP.image,
                onClick: () => {
                  if (this.props.toolExecute) this.props.toolExecute('Invert')
                },
                disabled: !this.props.anyOpen,
              })}
              {showDsa && onDsaSelect ? (
                <DsaMenu
                  enabled={!!dsaEnabled}
                  maskMode={dsaMaskMode}
                  frameCount={dsaFrameCount}
                  pixelShift={dsaPixelShift}
                  invertDSA={!!dsaInvert}
                  gain={dsaGain}
                  offset={dsaOffset}
                  noiseFloor={dsaNoiseFloor}
                  engineMode={dsaEngineMode || 'auto'}
                  synthMode={dsaSynthMode || 'minip'}
                  roadmap={dsaRoadmap}
                  onSelect={onDsaSelect}
                  menuProps={{
                    style: { zIndex: 160000 },
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: 'center', horizontal: 'left' },
                    transformOrigin: { vertical: 'center', horizontal: 'right' },
                  }}
                />
              ) : null}
              {this.renderToolButton({
                title: 'Histograma',
                tool: 'histogram',
                path: mdiChartHistogram,
                color: GROUP.image,
                onClick: () => {
                  if (this.props.onHistogram) this.props.onHistogram()
                },
                isActive: !!this.props.histogramOpen,
                disabled: !this.props.anyOpen,
              })}
              {showSec('qa')
                ? this.renderToolButton({
                title: 'QA de pantalla (GSDF / TG18-QC)',
                tool: 'gsdfqa',
                path: mdiMonitor,
                color: GROUP.image,
                onClick: () => {
                  if (this.props.onGsdfQa) this.props.onGsdfQa()
                },
                isActive: !!this.props.gsdfQaPassed,
              })
                : null}
              {this.renderToolButton({
                title: 'Capturar viewport (JPG/PNG)',
                tool: 'saveas',
                path: mdiCamera,
                color: GROUP.image,
                onClick: () => {
                  if (this.props.onCaptureViewport) this.props.onCaptureViewport()
                  else if (this.props.toolExecute) this.props.toolExecute('saveas')
                },
                disabled: !this.props.anyOpen,
              })}
              {this.renderToolButton({
                title: 'Compartir',
                tool: 'share',
                path: mdiShareVariant,
                color: GROUP.image,
                onClick: () => {
                  if (this.props.onShareViewport) this.props.onShareViewport()
                  else if (this.props.toolExecute) this.props.toolExecute('share')
                },
                disabled: !this.props.anyOpen,
              })}
              {this.props.onCopilotOpen
                ? this.renderToolButton({
                    title: 'UDV AI Copilot',
                    tool: 'copilot',
                    path: mdiAutoFix,
                    color: GROUP.image,
                    onClick: () => {
                      if (this.props.onCopilotOpen) this.props.onCopilotOpen()
                    },
                    disabled: !this.props.anyOpen,
                  })
                : null}
              <span
                style={{
                  display: 'flex',
                  width: '100%',
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                {this.props.onVoiceNoteToggle || this.props.onVoiceNoteOpenPanel
                  ? this.renderVoiceNoteButton()
                  : null}
              </span>
              {this.renderToolButton({
                title: 'Pan',
                tool: 'Pan',
                path: mdiCursorPointer,
                color: GROUP.image,
                onClick: () => this.handleModeTool('Pan'),
              })}
              {this.renderToolButton({
                title: 'Zoom',
                tool: 'Zoom',
                path: mdiMagnify,
                color: GROUP.image,
                onClick: () => this.handleModeTool('Zoom'),
              })}
            </>
          )
            : null}

          {showSec('estudio')
            ? this.renderGroup(
            'Estudio',
            GROUP.study,
            <>
              {this.renderToolButton({
                title: 'Metadatos DICOM (cabecera)',
                tool: 'header',
                path: mdiFileDocument,
                color: GROUP.study,
                onClick: () => {
                  if (this.props.onHeader) this.props.onHeader()
                },
                isActive: !!this.props.headerOpen,
                disabled: !this.props.anyOpen,
              })}
              {showRelatedStudies &&
                this.renderToolButton({
                  title: t('relatedStudies.menuTooltip'),
                  tool: 'related-studies',
                  path: mdiHistory,
                  color: GROUP.study,
                  onClick: () => {
                    if (onRelatedStudies) onRelatedStudies()
                  },
                  isActive: false,
                })}
              {showWorklist &&
                this.renderToolButton({
                  title: t('worklist.menuTooltip'),
                  tool: 'worklist',
                  path: mdiClipboardListOutline,
                  color: GROUP.study,
                  onClick: () => {
                    if (onWorklist) onWorklist()
                  },
                  isActive: false,
                })}
              {this.renderToolButton({
                title: compareMode
                  ? t('relatedStudies.compareModeOff')
                  : compareModeEnabled
                    ? t('relatedStudies.compareModeOn')
                    : t('relatedStudies.compareModeDisabled'),
                tool: 'compare-mode',
                path: mdiViewColumn,
                color: GROUP.study,
                onClick: () => {
                  if (onToggleCompareMode) onToggleCompareMode()
                },
                isActive: !!compareMode,
                disabled: !compareMode && !compareModeEnabled,
              })}
              {this.props.doseReport ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 44,
                    minHeight: 44,
                    padding: 4,
                    boxSizing: 'border-box',
                    width: '100%',
                  }}
                >
                  <DoseBadge
                    report={this.props.doseReport}
                    onClick={() => {
                      if (this.props.onDoseInfo) this.props.onDoseInfo()
                    }}
                  />
                </span>
              ) : (
                this.renderToolButton({
                  title: 'Info de dosis (radiación del estudio)',
                  tool: 'dose',
                  path: mdiInformationOutline,
                  color: GROUP.study,
                  onClick: () => {
                    if (this.props.onDoseInfo) this.props.onDoseInfo()
                  },
                  disabled: !this.props.anyOpen,
                })
              )}
            </>
          )
            : null}

          {showSec('medir')
            ? this.renderGroup(
            'Medición',
            GROUP.measure,
            <>
              {this.renderToolButton({
                title: 'Lupa: mantener pulsado y arrastrar',
                tool: 'Magnify',
                path: mdiCheckboxIntermediate,
                color: GROUP.measure,
                onClick: () => this.handleModeTool('Magnify'),
              })}
              {this.renderToolButton({
                title: 'Regla: pulsar, arrastrar y soltar',
                tool: 'Length',
                path: mdiRuler,
                color: GROUP.measure,
                onClick: () => this.handleModeTool('Length'),
              })}
              {this.renderToolButton({
                title: 'Sonda',
                tool: 'Probe',
                path: mdiEyedropper,
                color: GROUP.measure,
                onClick: () => this.handleModeTool('Probe'),
              })}
              {this.renderToolButton({
                title: 'Ángulo: 3 puntos (extremo → vértice → extremo)',
                tool: 'Angle',
                path: mdiAngleAcute,
                color: GROUP.measure,
                onClick: () => this.handleModeTool('Angle'),
              })}
              {this.renderToolButton({
                title: 'Cobb: 4 puntos (2 rectas)',
                tool: 'CobbAngle',
                path: mdiAngleRight,
                color: GROUP.measure,
                onClick: () => this.handleModeTool('CobbAngle'),
              })}
            </>
          )
            : null}

          {showSec('roi')
            ? this.renderGroup(
            'ROI',
            GROUP.roi,
            <>
              {this.renderToolButton({
                title: 'Elliptical Roi',
                tool: 'EllipticalRoi',
                path: mdiEllipse,
                color: GROUP.roi,
                onClick: () => this.handleModeTool('EllipticalRoi'),
              })}
              {this.renderToolButton({
                title: 'Rectangle Roi',
                tool: 'RectangleRoi',
                path: mdiRectangle,
                color: GROUP.roi,
                onClick: () => this.handleModeTool('RectangleRoi'),
              })}
              {this.renderToolButton({
                title: 'Freehand Roi',
                tool: 'FreehandRoi',
                path: mdiGesture,
                color: GROUP.roi,
                onClick: () => this.handleModeTool('FreehandRoi'),
              })}
            </>
          )
            : null}

          {showSec('render') ? this.renderRenderModeToggle() : null}

          {this.renderFooter()}
        </Drawer>
      </>
    )
  }
}

export default ToolsEdgePanel
